/**
 * AddListValueDialog
 *
 * Purpose: Small dialog for creating a new master-data value inline from a
 *          list/autocomplete field whose field definition allows creation.
 * Architecture: Controlled Material-UI Dialog. Calls mdmService.createValue on
 *          submit and returns the created value via onCreated. MDM cache
 *          invalidation is the caller's responsibility.
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { mdmService } from '../../services/mdmService';
import type { MasterDataValue } from '../../types/mdm';
import { SecondaryButton } from '../common/ActionButtons';

export interface AddListValueDialogProps {
  open: boolean;
  fielddefId: string;
  tenantId: string;
  fieldLabel: string;
  initialName?: string;
  onCancel: () => void;
  onCreated: (value: MasterDataValue) => void;
}

export const AddListValueDialog: React.FC<AddListValueDialogProps> = ({
  open,
  fielddefId,
  tenantId,
  fieldLabel,
  initialName,
  onCancel,
  onCreated,
}) => {
  const [name, setName] = useState<string>(initialName || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(initialName || '');
      setError(null);
      setSubmitting(false);
    }
  }, [open, initialName]);

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Name is required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const created = await mdmService.createValue({
        fielddef_id: fielddefId,
        tenant_id: tenantId,
        name: trimmed,
        is_active: true,
      });
      onCreated(created);
    } catch (err: any) {
      setError(err?.message || 'Failed to create value');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Add {fieldLabel}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !submitting) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            required
            autoFocus
            fullWidth
            disabled={submitting}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <SecondaryButton onClick={onCancel} disabled={submitting}>
          Cancel
        </SecondaryButton>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || !name.trim()}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddListValueDialog;
